import { useState, useRef } from 'react';
import axios from 'axios';
import { useLocale } from '../../utils/LocaleContext';
import { S } from '../regureStyles';
import { defaultEffectiveDate, annualPeriodEnd } from './offerDefaults';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Daily Sickness (IJM) Rate Tables ─────────────────────────
// From ARGOS tender — these are the ONLY correct rates
const IJMF_RATE_TABLES = {
  illness: {
    'Up to 40': { 30: 0.475, 60: 0.37, 90: 0.294 },
    '41-45': { 30: 0.646, 60: 0.56, 90: 0.437 },
    '46-50': { 30: 0.883, 60: 0.636, 90: 0.465 },
    '51-55': { 30: 1.14, 60: 0.839, 90: 0.684 },
    'Over 55': { 30: 1.187, 60: 0.931, 90: 0.712 },
  },
  'illness+accident': {
    'Up to 40': { 30: 0.617, 60: 0.484, 90: 0.38 },
    '41-45': { 30: 0.788, 60: 0.674, 90: 0.522 },
    '46-50': { 30: 1.045, 60: 0.798, 90: 0.627 },
    '51-55': { 30: 1.301, 60: 1.026, 90: 0.788 },
    'Over 55': { 30: 1.349, 60: 1.073, 90: 0.817 },
  },
};

function getAgeBand(numericAge) {
  const age = Number(numericAge);
  if (age <= 40) return 'Up to 40';
  if (age <= 45) return '41-45';
  if (age <= 50) return '46-50';
  if (age <= 55) return '51-55';
  return 'Over 55';
}

// ─── Motor ────────────────────────────────────────────────────────────
//
// Motor is DATA CAPTURE, not a rating engine. Every field here exists to
// reach the proposal document; nothing in this module derives a premium
// from any of it, and the premium stays what the underwriter typed. That
// is a deliberate boundary — a tariff nobody has approved, applied
// silently to a quote, is worse than an empty field.
//
// The keys are the contract with services/genericOfferDocument.js
// (motorSections). Renaming one here without renaming it there drops the
// value off the PDF silently, which is exactly the class of failure
// [[gotcha-ui-mapper-silently-drops-new-fields]] records.
export const MOTOR_COVERAGE_TYPES = [
  'Comprehensive',
  'Third Party Only',
  'Third Party Fire & Theft',
];

export const MOTOR_NCD_OPTIONS = ['0%', '10%', '20%', '30%', '40%', '50%'];

export const emptyMotor = () => ({
  vehicleMake: '',
  vehicleModel: '',
  yearOfManufacture: '',
  plateNumber: '',
  chassisNumber: '',
  vehicleValue: '',
  coverageType: MOTOR_COVERAGE_TYPES[0],
  effectiveDate: defaultEffectiveDate(),
  driverName: '',
  driverDateOfBirth: '',
  licenseNumber: '',
  noClaimsDiscount: MOTOR_NCD_OPTIONS[0],
});

/** Drop the blanks — an empty string on the PDF is a labelled gap. */
const compactMotor = (motor) => Object.fromEntries(
  Object.entries(motor)
    .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    .filter(([, v]) => v !== '' && v != null),
);

const useNewOfferFunctions = ({ onCreated, onClose }) => {
  const focusedRef = useRef(null);
  // The tenant's currency (or the user's override), resolved by the one
  // resolver in LocaleContext. Empty when nothing has been configured —
  // callers below render no currency rather than borrowing one.
  const { currency } = useLocale();
  const [client, setClient] = useState('');
  // broker: selected Broker ObjectId (empty when none). Sent alongside
  // the offer POST so the resulting offer carries a broker link, letting
  // the tester convert it to a policy without re-picking the broker
  // (Test Run 7-24: commission auto-gen requires Policy.broker to be an
  // ObjectId).
  const [broker, setBroker] = useState('');
  const [lob, setLob] = useState('');
  const [age, setAge] = useState('35');
  const [salary, setSalary] = useState('120000');
  const [waiting, setWaiting] = useState('30');
  const [riskType, setRiskType] = useState('illness');
  const [sumInsured, setSumInsured] = useState('500000');
  const [deductible, setDeductible] = useState('10000');
  const [docCount, setDocCount] = useState('3');
  const [calculated, setCalculated] = useState(null);
  // Motor's premium is typed, not calculated, so it needs its own input
  // state: `calculated` has to stay null while the box is empty (that is
  // what keeps the Create button disabled) and a raw string cannot do
  // both jobs.
  const [motor, setMotor] = useState(emptyMotor);
  const [manualPremium, setManualPremium] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setMotorField = (key, value) => setMotor(prev => ({ ...prev, [key]: value }));

  /** Manual premium entry. Mirrors into `calculated`, which is what the
   *  create call and the Create button both read, so the motor path needs
   *  no special case beyond this one function. */
  const setPremiumManually = (raw) => {
    setManualPremium(raw);
    const n = Number(raw);
    setCalculated(raw !== '' && Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null);
  };

  const calcPremium = () => {
    let p = 0;
    if (lob === 'E&O') p = Number(sumInsured) * 0.0016 + Number(docCount) * 300 + Number(deductible) * 0.01;
    if (lob === 'IJM') {
      const ageBand = getAgeBand(age);
      const rateKey = riskType === 'accident' ? 'illness+accident' : 'illness';
      const rate = IJMF_RATE_TABLES[rateKey]?.[ageBand]?.[Number(waiting)] || 0;
      p = Number(salary) * (rate / 100);
    }
    if (lob === 'PJ') p = Number(docCount) * 900 + 800;
    setCalculated(Math.max(1200, Math.round(p * 100) / 100));
  };

  const handleCreate = async () => {
    if (!client.trim()) {
      setError('Client name is required.');
      return;
    }
    if (!lob) {
      setError('Line of business is required.');
      return;
    }
    if (calculated === null) {
      setError(lob === 'MOTOR'
        ? 'Please enter the premium.'
        : 'Please calculate the premium first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(
        `${BASE_URL}/offers`,
        {
          client: client.trim(),
          lineOfBusiness: lob,
          premiumCalc: calculated,
          // The tenant's own currency, never the model default. Offer
          // .currency defaults to CHF, so a quote written by a tenant
          // configured in BHD used to print "CHF 450.00" on its proposal.
          //
          // Sent only when it actually RESOLVED. A currency stored on the
          // record wins over the tenant config on the generated document,
          // so writing an unresolved guess here would be worse than
          // writing nothing and letting the document read the config.
          ...(currency && { currency }),
          status: 'draft',
          params: {
            age, salary, waiting, riskType, sumInsured, deductible, docCount,
            ...(lob === 'MOTOR' && { motor: compactMotor(motor) }),
          },
          // Motor states its own annual period. Other generic products
          // send nothing and keep falling back to resolveContractEnd().
          ...(lob === 'MOTOR' && motor.effectiveDate && {
            coverage: {
              startDate: motor.effectiveDate,
              endDate: annualPeriodEnd(motor.effectiveDate),
            },
          }),
          ...(broker && { broker }),
        },
        { withCredentials: true },
      );
      onCreated && onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create quote. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 13px',
    background: '#fff',
    border: `1px solid ${S.borderLight}`,
    borderRadius: 10,
    fontSize: 13,
    color: S.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: S.textSecondary, display: 'block', marginBottom: 5 };

  return {
    focusedRef,
    currency,
    client,
    setClient,
    broker,
    setBroker,
    lob,
    setLob,
    age,
    setAge,
    salary,
    setSalary,
    waiting,
    setWaiting,
    riskType,
    setRiskType,
    sumInsured,
    setSumInsured,
    deductible,
    setDeductible,
    docCount,
    setDocCount,
    calculated,
    setCalculated,
    motor,
    setMotorField,
    manualPremium,
    setPremiumManually,
    saving,
    error,
    calcPremium,
    handleCreate,
    inputStyle,
    labelStyle,
  };
};

export default useNewOfferFunctions;
